import { useState, useEffect, useCallback } from 'react'

/**
 * Custom hook for managing carousel state and behavior
 * @param {number} totalImages - Total number of images in the carousel
 * @param {number} autoPlayDelay - Delay between auto-transitions in milliseconds
 * @returns {Object} Carousel state and control functions
 */
export const useCarousel = (totalImages, autoPlayDelay = 5000) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [direction, setDirection] = useState(1) // 1 for forward, -1 for backward

  const nextImage = useCallback(() => {
    setDirection(1)
    setCurrentIndex((prev) => (prev + 1) % totalImages)
  }, [totalImages])

  const prevImage = useCallback(() => {
    setDirection(-1)
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages)
  }, [totalImages])

  const goToImage = useCallback((index) => {
    setDirection(index > currentIndex ? 1 : -1)
    setCurrentIndex(index)
  }, [currentIndex])

  const togglePlayback = useCallback(() => {
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  // Auto-play effect
  useEffect(() => {
    if (!isPlaying || totalImages <= 1) return
    
    const interval = setInterval(() => {
      nextImage()
    }, autoPlayDelay)

    return () => clearInterval(interval)
  }, [isPlaying, nextImage, autoPlayDelay, totalImages])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          prevImage()
          break
        case 'ArrowRight':
          event.preventDefault()
          nextImage()
          break
        case ' ':
          event.preventDefault()
          togglePlayback()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextImage, prevImage, togglePlayback])

  return {
    currentIndex,
    nextImage,
    prevImage,
    goToImage,
    isPlaying,
    setIsPlaying,
    togglePlayback,
    direction
  }
}

/**
 * Custom hook for touch/swipe gestures on mobile devices
 * @param {Function} onSwipeLeft - Callback for left swipe
 * @param {Function} onSwipeRight - Callback for right swipe
 * @returns {Object} Touch event handlers
 */
export const useSwipeGestures = (onSwipeLeft, onSwipeRight) => {
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  const minSwipeDistance = 50

  const onTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && onSwipeLeft) {
      onSwipeLeft()
    }
    if (isRightSwipe && onSwipeRight) {
      onSwipeRight()
    }
  }

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  }
}